import * as React from 'react';
import injectSheet from 'react-jss';
import { Button, Form, Modal, Input, message, Icon, Tooltip } from 'antd';
import Select from 'src/components/common/Select';
import { connect } from 'react-redux';
import { v4 as uuid } from 'uuid';
import { quickAddContainer, editContainer, editShipment } from 'src/api/shipmentTracking';
import { KeyLabel, StylesProps } from 'src/Library/Types';
import { FormComponentProps } from 'antd/lib/form';
import { SHIPMENT_TYPE } from './ContainerUtils';
import { CardData } from './Interfaces/Shipment.interface';
import { getUniqFromArray, isValidArray, clone } from 'src/Library/Helper';
import { tokens } from 'src/design-system';
import { useGenericState } from 'src/Library/UseGenericState';
import * as LogRocket from 'logrocket';


const styles = () => ({
  modalSubmitButton: {
    backgroundColor: tokens.colors.primary,
    borderRadius: tokens.radius.sm,
    color: tokens.colors.bgWhite,
    fontSize: tokens.fontSize.h3,
    height: '40px',
    width: '94px',
    '& span': {
      color: tokens.colors.bgWhite,
      fontSize: tokens.fontSize.h3,
      textAlign: 'center',
    },
  },
  modalCancelButton: {
    height: '40px',
    width: '94px',
    border: `1px solid ${tokens.colors.border}`,
    borderRadius: tokens.radius.sm,
    backgroundColor: tokens.colors.bgSubtle,
    marginRight: tokens.spacing.sm,
    '& span': {
      color: tokens.colors.textSecondary,
      fontSize: tokens.fontSize.h3,
      textAlign: 'center',
    },
  },
  modal: {
    '& .ant-modal-body': {
      padding: `${tokens.spacing.sm + 2}px ${tokens.spacing.lg}px`,
      minHeight: '350px',
    },
    '& .duplicate-container-item': {
      border: `1px solid ${tokens.colors.error}`,
    },
    '& .duplicate-container-item.ant-select':{
      border: `1px solid ${tokens.colors.error}`,
      borderRadius: tokens.radius.md,
    },
    '& .ant-modal-footer': {
      padding: '17px 20px',
    },
  },
  editModal: {
    '& .ant-modal-body': {
      minHeight: '200px',
    },
  },
  row: {
    width: '100%',
    marginBottom: '0px',
    display: 'flex',
    flexDirection: 'row',
  },
});

const ALPHA_NUMERIC_REGEX = /^[a-z0-9]+$/i;
const CHAR_LIMIT = 25;

interface ReduxProps {
  containerSizes: string[];
  containerTypesKeyVal: KeyLabel[];
}

interface IProps extends StylesProps<ReturnType<typeof styles>>, ReduxProps, FormComponentProps {
  isEdit: boolean;
  teamSelected: [];
  show: boolean;
  container: CardData;
  trackingRequestId: string;
  shipmentShippingLine: any;
  movementType: string;
  accessorType: string;
  containerDetails;
  teamDetails: [];
  onClose: (success? : boolean) => void;
}

interface StateProps {
  containers;
  confirmLoading: boolean;
}

interface Container {
  containerNumber: string;
  containerSize: string;
  containerType: string;
}

const BLContainerDetailModal = (props: IProps) => {
  const [state, setState] = useGenericState<StateProps>({
    containers: [{ id: uuid() }], // uuid is used to check which container item state has been changed
    confirmLoading: false,
  });
  const {
    isEdit,
    form,
    show,
    classes,
    onClose,
    trackingRequestId,
    teamSelected,
    shipmentShippingLine,
    movementType,
    accessorType,
    containerSizes,
    containerDetails,
    containerTypesKeyVal,
    teamDetails,
  } = props;

  const {
    containers,
    confirmLoading,
  } = state;

  function handleModalSubmit() {
    form.validateFields((err, values) => {
      if (err) {
        message.error('Container Number is required, Duplicate containers are not allowed');
        return;
      }
      prepareRequest(values.containers);
    });
  }

  async function prepareRequest(formValues: Container[]) {
    setState({ confirmLoading: true });
    const containerNumberList = clone(containers);
    let containerSizeType = {};
    //geting teamIds
    const teamIds : string[] = [];
    if (teamDetails)
      props.teamDetails.forEach((item:any)=>teamIds.push(item.teamId));
    if (teamSelected)
      props.teamSelected.forEach((item:any)=>teamIds.push(item));

    containerNumberList.forEach((container) => {
      container.id = undefined;
      container.movementType = movementType;
      container.shippingLine = shipmentShippingLine.id;
      container.trackingRequestId = trackingRequestId;
      container.accessType = accessorType;
      container.trackingType = SHIPMENT_TYPE.CN;
    });
    containers.forEach((container) => containerSizeType[container.containerNumber] = {
      containerType: formValues[container.id].containerType,
      containerSize: formValues[container.id].containerSize,
    });
    React.useEffect
    const response = await editShipment({
      containerSizeType,
      ...containerNumberList[0],
      movementType,
      id: trackingRequestId,
      shippingLine: shipmentShippingLine?.id,
      accessType: accessorType,
      // teamId: teamSelected,
      customFields: undefined,
      teamIds: teamIds,
      containerNumbers: [
        ...(containerNumberList || []),
        ...(containerDetails || [])
      ]?.map(item => (item?.containerNumber))
      .filter(container => !!container) || [],
    });
    setState({ confirmLoading: false });
    if (response.isSuccess) {
      if (response?.data?.failureArray) {
        message.error(response.data.failureArray[0]?.err?.message);
        return;
      }
      let successMsg;
      if (isEdit) {
        successMsg = `Container ${formValues[0].containerNumber} has been updated sucessfully.`;
      } else {
        successMsg = `${containerNumberList.length} Container(s) has been added sucessfully.`;
      }
      message.success(successMsg);
      LogRocket.track(successMsg);
      onClose(true);
    } else {
      message.error(response.errorMessage);
      LogRocket.track(response.errorMessage);
    }
  }

  function handleAddMore() {
    setState({ containers: [...containers, { id: uuid() }]});
  }

  function deleteContainer(key: string) {
    const cloneContainers = [...containers].filter(item => item.id !== key);
    setState({ containers: cloneContainers });
  }

  const containerNumberValidator = (rule, value, cb, idx) => {
    containers?.forEach((item, ind) => {
      if (item.id !== idx && value && item.containerNumber === value) {
        return cb(' ');
      }
    });
    return cb();
  };

  const handleChange = (key, value, id) => {
    setState({
      containers: [...containers.map(item => {
        if (item.id === key) {
          return {
            ...item,
            [id]: value,
          };
        }
        return item;
      })]
    })
  };

  const renderContainerNumber = (key: string, index: number) => {
    return <Form.Item >
      {form.getFieldDecorator(`containers[${key}]['containerNumber']`, {
        rules: [
          { required: true, message: 'Required' },
          { pattern: /^[a-zA-Z]{4}[0-9]{6,}$/, message: 'Wrong Container Number' },
          { max: CHAR_LIMIT, message: `Length cannot be more than ${CHAR_LIMIT}` },
          { pattern: /^[^\s]+$/, message: 'No whitespace allowed' },
        ]})(<Input
          placeholder="Container Number"
          onChange={(e) => handleChange(key, e.target.value, 'containerNumber')}
          style={{ width: '157px' }}
          className={containers[index].isDuplicate ? 'duplicate-container-item' : ''}
          disabled={isEdit}
        />)}
      </Form.Item>;
  };

  const renderContainerType = (key: string, index: number) => {
    return <Form.Item >
      {form.getFieldDecorator(`containers[${key}]['containerType']`, {
        initialValue: 'STANDARD',
      })(<Select
        placeholder="Type"
        onChange={(e) => handleChange(key, e, 'containerType')}
        style={{ width: '157px', marginLeft: '8px' }}
        className={containers[index].isDuplicate ? 'duplicate-container-item' : ''}>
        {containerTypesKeyVal.map(type =>
          <Select.Option
            key={type.key}>
            {type.label}
          </Select.Option>)}
      </Select>) }
    </Form.Item>;
  };

  const renderContainerSize = (key: string, index: number) => {
    return <Form.Item >
      {form.getFieldDecorator(`containers[${key}]['containerSize']`, {
        initialValue: '20DV',
      })(<Select
        placeholder="Size"
        onChange={(e) => handleChange(key, e, 'containerSize')}
        style={{ width: '85px', marginLeft: '8px' }}
        className={containers[index].isDuplicate ? 'duplicate-container-item' : ''}>
        {containerSizes.map(size =>
          <Select.Option
            key={size}>
            {size}
          </Select.Option>)}
      </Select>)}
    </Form.Item>;
  };

  const renderDelete = (key) => {
    if (containers?.length === 1) return null;
    return <Tooltip
      title="Delete this container">
      <Icon
        type="close"
        style={{
          cursor: 'pointer',
          color: tokens.colors.error,
          marginTop: '13px',
          marginLeft: '13px',
          fontWeight: 700,
        }}
        onClick={() => deleteContainer(key)}
      />
    </Tooltip>;
  };

  const renderContainerFormFields = () => {
    return containers?.map((item, index: number) => {
      return <div
        key={item.id}
        className={classes.row}
        style={{
          height: '30px',
          marginBottom: '8px',
          marginTop: index === 0 ? '-10px' : '0px',
        }}>
        {renderContainerNumber(item.id, index)}
        {renderContainerType(item.id, index)}
        {renderContainerSize(item.id, index)}
        {renderDelete(item.id)}
      </div>;
    });
  };

  const renderAddMoreButton = () => {
    return <div
      className={classes.row}
      style={{ marginTop: '30px' }}>
      <Icon type="plus"
        style={{
          height: '12px',
          width: '18px',
          color: tokens.colors.primary,
          paddingTop: '2px',
        }}/>
      <a type="plus-circle"
        style={{
          cursor: 'pointer',
          color: tokens.colors.primary,
          marginLeft: '5px',
          fontSize: tokens.fontSize.small,
        }}
        onClick={handleAddMore}>
        Add More
      </a>
    </div>;
  };

  const renderModalBody = () => {
    return <React.Fragment>
      <p style={{
        color: tokens.colors.textSecondary,
        fontSize: tokens.fontSize.small,
        fontWeight: tokens.fontWeight.semibold,
      }}>
        Container Details
      </p>
      {renderContainerFormFields()}
      {!isEdit && renderAddMoreButton()}
    </React.Fragment>;
  };

  const renderModalFooter = () => {
    const submitButton = (<Button key="submit"
      type="primary"
      className={classes.modalSubmitButton}
      loading = {confirmLoading}
      onClick={handleModalSubmit}>
      {isEdit ? 'Update' : 'Add'}
    </Button>);
    if (isEdit) {
      const cancelButton = (<Button key="back"
        className={classes.modalCancelButton}
        onClick={() => onClose()}>
        Cancel
      </Button>);
      return [cancelButton, submitButton];
    }
    return [submitButton];
  };

  return <Modal
    className={[classes.modal, isEdit ? classes.editModal : ''].join(' ')}
    title={`${isEdit ? 'Edit' : 'Add'} Container`}
    visible={show}
    onCancel={() => onClose()}
    footer={renderModalFooter()}>
    {renderModalBody()}
  </Modal>;
};

const mapStateToProps = ({ staticData }) => {
  return {
    containerSizes: staticData.containerSize,
    containerTypesKeyVal: staticData.containerTypesKeyVal,
  };
};

export default injectSheet(styles)(connect(mapStateToProps)(Form.create({})(
BLContainerDetailModal)));
